<template>
  <MapBoundary
    ref="mapRef"
    width="100%"
    :height="height"
    :sights="sights"
    hide-popup
    @is-view-sights="updateSights"
    @click-on-marker="useMarker"
  />
  <div class="sights-on-map">
    <UiSlider
      v-bind="cardProps"
      ref="slider"
    >
      <template #slide="{ slide }">
        <div
          v-if="isMapSlide(slide) && slide.id === 0"
          class="viewMore"
        >
          <div class="text">Переместите или отдалите карту, чтобы увидеть больше достопримечательностей</div>
          <UiButton
            v-if="sights?.length"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            text="Показать все"
            class="btn-map-more"
            data-test="on-map-more"
            :size="UiButtonSize.S"
            @clicked="viewAll"
          />
        </div>
        <SightsMapCard
          v-else-if="isMapSlide(slide)"
          :key="`map-card-${slide.id}`"
          :data-id="slide.id"
          class="card"
          :sight="slide"
        />
      </template>
    </UiSlider>
  </div>
  <div
    class="close-button"
    @click="close"
  >
    <IconBase name="close-icon" />
  </div>
</template>

<script lang="ts" setup>
  import { UiButton, UiButtonVariant, UiButtonColorMode, UiButtonSize } from '@experience/core-button-ui';
  import { UiSlider } from '@experience/core-slider-ui';
  import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';

  import IconBase from '@/shared/icons/IconBase.vue';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import MapBoundary from '@/shared/ui/MapBoundary.vue';
  import SightsMapCard from '@/shared/ui/SightsMapCard.vue';

  const mapReference = useTemplateRef('mapRef');
  const sliderRef = useTemplateRef('slider');

  const nodes = ref<NodeListOf<HTMLElement> | null>(null);
  const observer = ref<IntersectionObserver | null>(null);

  const emit = defineEmits(['hoverUpdate', 'isViewSights', 'view-all', 'close']);

  interface Props {
    sights?: CityTagsAdvanced[];
    isViewMore?: boolean;
  }

  const isMapSlide = (value: unknown): value is CityTagsAdvanced => {
    if (typeof value !== 'object' || value === null) {
      return false;
    }

    const slide = value as Record<string, unknown>;

    return (
      typeof slide.id === 'number' &&
      typeof slide.slug === 'string' &&
      typeof slide.name === 'string' &&
      typeof slide.description === 'string' &&
      typeof slide.address === 'string' &&
      typeof slide.excursions_count === 'number' &&
      typeof slide.guides_count === 'number' &&
      typeof slide.experience_main_photo_url === 'string' &&
      typeof slide.guide_avatar_url === 'string' &&
      typeof slide.citytag_url === 'string' &&
      typeof slide.latitude === 'number' &&
      typeof slide.longitude === 'number' &&
      typeof slide.cover_photo === 'object' &&
      slide.cover_photo !== null
    );
  };

  const props = withDefaults(defineProps<Props>(), {
    isViewMore: false,
    sights: () => [],
  });

  onMounted(() => {
    createObs();
  });

  const isViewedPoints = () => {
    return props.sights.filter((el) => el.isView);
  };

  const filter = computed<CityTagsAdvanced[]>(() => {
    const isViewed = isViewedPoints();
    const mock = {
      id: 0,
      slug: '',
      name: '',
      description: '',
      address: '',
      excursions_count: 0,
      guides_count: 0,
      experience_main_photo_url: '',
      guide_avatar_url: '',
      citytag_url: '',
      latitude: 0,
      longitude: 0,
      cover_photo: {},
    };

    if (!isViewed.length) {
      return [mock];
    }

    return props.isViewMore ? [...isViewed, mock] : isViewed;
  });

  const updateSights = (data: CityTagsAdvanced) => {
    emit('isViewSights', data);
    sliderRef.value?.goToSlide(0);
  };

  const viewAll = () => {
    mapReference.value?.useZoom();
  };

  const height = computed(() => {
    return document.documentElement.clientHeight ? `${String(document.documentElement.clientHeight - 184)}px` : '100%';
  });

  const initObs = (options: IntersectionObserverInit): IntersectionObserver => {
    return new IntersectionObserver((entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          const id = (entry.target as HTMLElement).dataset.id;

          if (id) {
            emit('hoverUpdate', Number(id));
          }
        }
      });
    }, options);
  };

  const createObs = (): void => {
    void nextTick(() => {
      nodes.value = document.querySelectorAll('.card');

      const options: IntersectionObserverInit = {
        root: null,
        rootMargin: '48px',
        threshold: 0.8,
      };

      observer.value = initObs(options);

      nodes.value.forEach((i: HTMLElement) => {
        observer.value?.observe(i);
      });
    });
  };

  const unObserve = (): void => {
    if (nodes.value) {
      nodes.value.forEach((i: HTMLElement) => {
        if (observer.value) {
          observer.value.unobserve(i);
        }
      });
    }
  };

  const cardProps = computed(() => {
    const cardWidth = document.documentElement.clientWidth - 48;

    return {
      cardWidth,
      spaceBetween: 16,
      slides: filter.value,
      offsetWidthCssMode: '16px',
      options: { slidesOffsetAfter: filter.value.length === 1 ? 0 : 50 },
    };
  });

  // НУЖНО НАЙТИ ДРУГОЙ СПОСОБ ВЫБИРАТЬ АКТИВНЫЙ СЛАЙД
  watch(filter, () => {
    unObserve();
    createObs();
  });

  const useMarker = (marker: CityTagsAdvanced) => {
    const index = filter.value.findIndex((el) => el.id === marker.id);

    if (typeof index === 'number') {
      sliderRef.value?.goToSlide(index);
    }
  };

  const close = () => {
    props.sights.forEach((el) => {
      el.isView = false;
    });
    emit('close');
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;

  .sights-on-map {
    width: 100%;
    height: 184px;
    padding: var(--alias-space-12) 0;
  }

  .text {
    width: 240px;

    @include legacy-typography.base-300-300-regular;
  }

  .btn-map-more {
    margin-top: var(--alias-space-8);
  }

  .close-button {
    position: absolute;
    top: 16px !important;
    right: 16px !important;
    z-index: 22;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--alias-space-6);
    background: var(--alias-color-background-default);
    border-radius: var(--alias-radius-8, 8px);
  }
</style>
